import React from 'react';
import { CheckCircle, AlertCircle, Circle, ChevronRight, Info, Eye } from 'lucide-react';
import { ComplianceItem, ComplianceChecklist, ExpandedItems, CategoryStats } from '../data/complianceData';

interface Props {
  categories: { [key: string]: string[] };
  filteredData: ComplianceItem[];
  categoryStats: CategoryStats;
  expandedItems: ExpandedItems;
  onToggleExpand: (id: string) => void;
  checklist: ComplianceChecklist;
  onStatusChange: (id: string, status: string) => void;
  onSelectItem: (item: ComplianceItem) => void;
}

const ChecklistView: React.FC<Props> = ({
  categories, filteredData, categoryStats,
  expandedItems, onToggleExpand,
  checklist, onStatusChange, onSelectItem,
}) => (
  <div>
    {Object.entries(categories).map(([category, ids]) => {
      const categoryRequirements = filteredData.filter(item => ids.includes(item.id));
      if (categoryRequirements.length === 0) return null;
      const stats = categoryStats[category];

      return (
        <div key={category} className="mb-8">
          <div className="flex items-center justify-between mb-4 px-3">
            <div className="flex items-center gap-3">
              <input type="checkbox" className="w-5 h-5 rounded border-gray-300" readOnly />
              <h3 className="text-lg font-semibold text-gray-800">
                {category}
                <span className="ml-3 text-sm font-normal text-gray-500">
                  ({stats?.completed || 0}/{categoryRequirements.length} complete)
                </span>
              </h3>
            </div>
            <div className="flex items-center gap-3">
              <div className="mr-3 text-sm text-gray-600">{stats?.percentage || 0}%</div>
              <div className="w-36 bg-gray-200 rounded-full h-2.5">
                <div
                  className="bg-blue-600 h-2.5 rounded-full"
                  style={{ width: `${stats?.percentage || 0}%` }}
                />
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400 ml-2" />
            </div>
          </div>

          <div className="rounded-lg border border-gray-200 overflow-hidden">
            {categoryRequirements.map((item) => (
              <div
                key={item.id}
                className="border-t border-gray-200 first:border-t-0 px-6 py-5 hover:bg-gray-50"
              >
                <div
                  className="flex items-center justify-between cursor-pointer"
                  onClick={() => onToggleExpand(item.id)}
                >
                  <div className="flex items-start gap-4">
                    {checklist[item.id] === 'completed' ? (
                      <CheckCircle className="w-6 h-6 text-green-500 mt-1 flex-shrink-0" />
                    ) : checklist[item.id] === 'in-progress' ? (
                      <AlertCircle className="w-6 h-6 text-yellow-500 mt-1 flex-shrink-0" />
                    ) : (
                      <Circle className="w-6 h-6 text-gray-300 mt-1 flex-shrink-0" />
                    )}
                    <div>
                      <div className="font-medium text-gray-900 mb-2 text-lg">
                        {item.id} - {item.title}
                      </div>
                      <div className="text-sm text-gray-600 flex gap-8">
                        <span className="whitespace-nowrap"><strong>ISO:</strong> {item.iso27001.split(',')[0]}...</span>
                        <span className="whitespace-nowrap"><strong>NIST:</strong> {item.nist.split(',')[0]}...</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <select
                      value={checklist[item.id] || 'not-started'}
                      onChange={(e) => onStatusChange(item.id, e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      className="py-2 px-4 text-sm border border-gray-300 rounded-md"
                    >
                      <option value="not-started">Not Started</option>
                      <option value="in-progress">In Progress</option>
                      <option value="completed">Completed</option>
                    </select>
                    <button
                      className="text-blue-600 hover:text-blue-800 p-2"
                      onClick={(e) => { e.stopPropagation(); onSelectItem(item); }}
                    >
                      <Info className="w-5 h-5" />
                    </button>
                    <ChevronRight
                      className={`w-6 h-6 text-gray-400 transition-transform ${expandedItems[item.id] ? 'rotate-90' : ''}`}
                    />
                  </div>
                </div>

                {expandedItems[item.id] && (
                  <div className="mt-6 pt-6 border-t border-gray-200 pl-10">
                    <div className="grid grid-cols-2 gap-8 mb-6">
                      <div>
                        <h5 className="text-base font-medium text-gray-700 mb-4">International Standards</h5>
                        <div className="space-y-3 text-sm">
                          {[
                            ['ISO 27001:', item.iso27001],
                            ['NIST CSF:', item.nist],
                            ['ETSI:', item.etsi],
                            ['CEN:', item.cen],
                          ].map(([label, val]) => (
                            <div key={label} className="mb-2 flex">
                              <strong className="inline-block w-28 text-gray-700">{label}</strong>
                              <span className="text-gray-600">{val}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div>
                        <h5 className="text-base font-medium text-gray-700 mb-4">National Frameworks</h5>
                        <div className="space-y-3 text-sm">
                          {[
                            ['🇧🇪 Belgium:', item.national.BE.split(',')[0]],
                            ['🇫🇮 Finland:', item.national.FI.split(',')[0]],
                            ['🇬🇷 Greece:', item.national.EL.split(',')[0]],
                            ['🇪🇸 Spain:', item.national.ES.split(',')[0]],
                          ].map(([label, val]) => (
                            <div key={label} className="mb-2 flex">
                              <strong className="inline-block w-28 text-gray-700">{label}</strong>
                              <span className="text-gray-600">{val}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-between items-center mt-5 pt-4 border-t border-gray-100">
                      <button
                        onClick={(e) => { e.stopPropagation(); onSelectItem(item); }}
                        className="flex items-center gap-2 text-blue-600 hover:text-blue-800 bg-blue-50 px-5 py-2.5 rounded"
                      >
                        <Eye size={18} />
                        <span>View Full Details</span>
                      </button>
                      <select
                        value={checklist[item.id] || 'not-started'}
                        onChange={(e) => onStatusChange(item.id, e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        className="py-2 px-4 text-sm border border-gray-300 rounded-md"
                      >
                        <option value="not-started">Not Started</option>
                        <option value="in-progress">In Progress</option>
                        <option value="completed">Completed</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      );
    })}
  </div>
);

export default ChecklistView;
